/* eslint-disable react/prop-types */
import { useLazyQuery } from "@apollo/client";
import { Empty, Spin } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaChevronDown } from "react-icons/fa";
import { withAsyncPaginate } from "react-select-async-paginate";
import Creatable from "react-select/creatable";

const COMPONENTS = {
  IndicatorSeparator: null,
  DropdownIndicator: () => <FaChevronDown className="dropdown-indicator__icon" />,
  LoadingIndicator: () => <AiOutlineLoading3Quarters />,
  LoadingMessage: () => <Spin className="dropdown-loading__icon" />,
  NoOptionsMessage: () => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />,
};

const CreatableWithAsyncPaginate = withAsyncPaginate(Creatable) as unknown as React.ComponentType<Record<string, unknown>>;

interface CreatableProps {
  onCreateOption?: (value: string) => void;
  components?: Record<string, unknown>;
  queryOptions: {
    query: import("@apollo/client").DocumentNode;
    client: import("@apollo/client").ApolloClient<unknown>;
    dataName: string;
    variables: Record<string, unknown> & { filter?: Record<string, unknown> };
    extraOptions?: Array<{ label: string; value: unknown }>;
  };
  isDisabled?: boolean;
  defaultFirstItem?: boolean;
  onChange?: (value: unknown) => void;
  optionsFormat: (item: Record<string, unknown>) => { label: string; value: unknown };
  hasQueryFilter?: boolean;
  noPagination?: boolean;
  value?: unknown;
  [key: string]: unknown;
}

export function CreatableAsyncPaginate(props: CreatableProps) {
  const { t } = useTranslation();
  const {
    onCreateOption,
    components,
    queryOptions,
    isDisabled,
    defaultFirstItem,
    onChange,
    optionsFormat,
    hasQueryFilter,
    noPagination,
    value,
    ...other
  } = props;

  const [options, setOPtions] = useState<Record<any, any>[]>([]);
  const [selectedItem, setSelectedItem] = useState<any>();

  const [listQuery] = useLazyQuery<Record<string, any>, Record<string, any>>(queryOptions.query, {
    client: queryOptions.client,
    notifyOnNetworkStatusChange: true,
  });

  const loadOptions = async (search = "", prevOptions: unknown[]) => {
    let hasMore = false;
    let filter: Record<any, any> = { search };
    if (hasQueryFilter) {
      filter = { filter: { ...queryOptions.variables.filter, search } };
    }

    let newOptions: Record<any, any>[] = [];
    const variables = {
      ...queryOptions.variables,
      offset: prevOptions.length,
      ...filter,
    };
    const response = await listQuery({ variables });

    const data = response?.data as Record<string, { results?: unknown[]; totalCount?: number } | unknown[]> | undefined;
    let resultsList: unknown = data?.[queryOptions.dataName];
    if (!noPagination) {
      resultsList = (resultsList as { results: unknown[] }).results;
    }
    newOptions = (resultsList as Record<string, unknown>[]).map((item) => optionsFormat(item)) as Record<any, any>[];

    if (queryOptions.extraOptions) {
      newOptions.unshift(...(queryOptions.extraOptions as Record<any, any>[]));
    }
    if (!noPagination) {
      const total = (data?.[queryOptions.dataName] as { totalCount?: number })?.totalCount ?? 0;
      hasMore = prevOptions.length + newOptions.length < total;
    }

    setOPtions(newOptions);
    return { options: newOptions, hasMore };
  };

  const handleOnCreateOption = (inputValue: string) => {
    if (onCreateOption) {
      onCreateOption(inputValue);
    }
  };

  useEffect(() => {
    if (!value && !selectedItem && options.length > 0 && defaultFirstItem) {
      setSelectedItem(options[0]);
      onChange && onChange(options[0]);
    } else {
      setSelectedItem(value);
    }
  }, [value, options]);

  return (
    <CreatableWithAsyncPaginate
      isClearable
      placeholder=""
      value={selectedItem}
      debounceTimeout={750}
      menuPosition={"fixed"}
      classNamePrefix="async-createable"
      isDisabled={isDisabled}
      loadOptions={loadOptions}
      createOptionPosition="first"
      loadOptionsOnMenuOpen={true}
      onCreateOption={handleOnCreateOption}
      components={{
        ...COMPONENTS,
        ...components,
      }}
      formatCreateLabel={(value: string) => {
        return `${t("Create")} ${value}`;
      }}
      onChange={(data: unknown) => {
        onChange && onChange(data);
        setSelectedItem(data);
      }}
      theme={(theme: Record<string, unknown>) => ({
        ...theme,
        borderRadius: 2,
      })}
      styles={{
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
        control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
          ...provided,
          background: isDisabled ? "#f5f5f5" : "#fff",
          borderColor: "#d9d9d9",
          minHeight: "32px",
          height: "32px",
          boxShadow: state.isFocused ? "0 0 0 2px rgba(@primary-color, 0.2)" : null,
          "&:hover": {
            borderColor: "@primary-color",
          },
        }),
        valueContainer: (provided: Record<string, unknown>) => ({
          ...provided,
          height: "32px",
          padding: "0 6px",
        }),
        input: (provided: Record<string, unknown>) => ({
          ...provided,
          margin: "0px",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
        indicatorsContainer: (provided: Record<string, unknown>) => ({
          ...provided,
          height: "32px",
        }),
        option: (provided: Record<string, unknown>, state: { isSelected: boolean }) => ({
          ...provided,
          backgroundColor: state.isSelected ? "@primary-color" : "inherit",
          "&:hover": {
            backgroundColor: state.isSelected ? "@primary-color" : "rgba(@primary-color, 0.2)",
          },
        }),
        dropdownIndicator: (provided: Record<string, unknown>) => ({
          ...provided,
          svg: {
            fill: "red",
          },
        }),
        placeholder: (defaultStyles: Record<string, unknown>) => {
          return {
            ...defaultStyles,
            color: "#bdbdbd",
          };
        },
      }}
      {...other}
    />
  );
}
